import React from 'react';
import * as dates from '@simplybenefits/sb-dates';
import * as addressTools from '@simplybenefits/sb-address';
import { Button, Text, Tooltip, Theme, i18n, tokens, localizationMaps } from '@simplybenefits/sb-ui-library';
import Grid from "@mui/material/Grid";
import { styled } from '@mui/system';
import { useTheme } from '@mui/material/styles';
import InfoIcon from "@mui/icons-material/Info";

const StyledHeader = styled('div')(({ theme }) => ({
    display: 'flex',
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'row',
    backgroundColor: theme.palette.primary.main,
    padding: "10px"
}));

/**
 * @param x
 * @private
 */
function _numberWithCommas(x) {
    return x.toString()
        .replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

/**
 * @param x
 * @private
 */
function _parsePhoneNumber(x) {
    return x.replace(/\D+/g, '')
        .replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3');
}

/**
 * @param handleOnEdit
 * @returns {*}
 * @constructor
 */
const HeaderRow = ({ onEdit, userDivision }) => {
    const theme = useTheme();
    const showEditButton = userDivision.access !== 'Read';
    return (
        <StyledHeader>
            <div style={{ flex: 1 }}>
                <Text mediumLarge bold color={ Theme.colors.surface }>{ i18n.t(tokens.labels.employees.personalInformation) }</Text>
            </div>

            {
                showEditButton &&
                    <Button
                        transparent
                        color="primary"
                        customStyle={{ color: '#FFF', "&:hover": { backgroundColor: theme.palette.primary.dark } }}
                        onClick={onEdit}
                    >
                        { i18n.t(tokens.buttons.editDetails) }
                    </Button>
            }
        </StyledHeader>
    );
};

/**
 * @param meta
 * @param coverage
 * @param onEdit
 * @returns {*}
 * @constructor
 */
const PersonalInfoBlock = ({ meta, coverage, onEdit, classData, divisions }) => {
    const theme = useTheme();
    const userDivision = divisions[meta.divisionID];
    const address = addressTools.parseAddress(meta.mailingAddress || '');
    const hasWaived = meta.hasWaived || '';

    const styles = {
        container: {
            flex: 1,
            minWidth: 290,
            border: "2px solid #F1F1F1",
            borderRadius: 5,
            backgroundColor: '#FFF'
        },
        row: {
            margin: "10px 0",
            padding: "0 20px",
            alignItems: "baseline"
        },
        block: {
            display: 'flex',
            flexWrap: 'wrap',
            flexDirection: 'column'
        },
        effectiveDateDisplay: {
            display: "flex",
            flexDirection: "row",
            alignItems: 'center',
            backgroundColor: theme.palette.primary.light,
            padding: 15,
            width: "95%",
            borderLeft: `3.5px solid ${ theme.palette.primary.main }`
        }
    };

    return (
        <div style={ styles.container }>
            <HeaderRow onEdit={ onEdit } userDivision={ userDivision } />

            <Grid
                container
                direction="row"
                alignItems="center"
                spacing={2}
                sx={ styles.row }
                wrap="wrap"
            >
                <Grid item xs={6} md={4} lg={3} style={{ padding: 18 }}>
                    <div style={ styles.block }>
                        <Text bold small>
                            { i18n.t(tokens.labels.firstname) }
                        </Text>

                        <Text small>
                            { meta.firstname }
                        </Text>
                    </div>
                </Grid>

                <Grid item xs={6} md={4} lg={3} style={{ padding: 18 }}>
                    <div style={ styles.block }>
                        <Text bold small>
                            { i18n.t(tokens.labels.lastname) }
                        </Text>

                        <Text small>
                            { meta.lastname }
                        </Text>
                    </div>
                </Grid>

                <Grid item xs={6} md={4} lg={3} style={{ padding: 18 }}>
                    <div style={ styles.block }>
                        <Text bold small>
                            { i18n.t(tokens.labels.signup.certificateNumber) }
                        </Text>

                        <Text small>
                            { meta.cert || i18n.t(tokens.labels.employees.pending) }
                        </Text>
                    </div>
                </Grid>

                <Grid item xs={6} md={4} lg={3} style={{ padding: 18 }}>
                    <div style={ styles.block }>
                        <Text bold small>
                            { i18n.t(tokens.labels.employees.hireDate) }
                        </Text>

                        <Text small>
                            { dates.getStartOfDay(meta.hireDate, 'll', i18n.locale) }
                        </Text>
                    </div>
                </Grid>

                <Grid item xs={6} md={4} lg={3} style={{ padding: 18 }} >
                    <div style={ styles.block }>
                        <Text bold small>
                            { i18n.t(tokens.labels.employees.enrollmentDate) }
                        </Text>

                        <Text small>
                            { meta.enrollmentDate ? dates.getStartOfDay(meta.enrollmentDate, 'll', i18n.locale) : i18n.t(tokens.labels.employees.pending) }
                        </Text>
                    </div>
                </Grid>

                <Grid item xs={6} md={4} lg={3} style={{ padding: 18 }}>
                    <div style={ styles.block }>
                        <Text bold small>
                            { i18n.t(tokens.titles.division) }
                        </Text>

                        {
                            divisions[meta.divisionID].description ?
                                <Tooltip
                                    tip={ divisions[meta.divisionID].description }
                                    placement="top-start"
                                >
                                    <Text small>
                                        { meta.divisionName }
                                    </Text>
                                </Tooltip>
                                :
                                <Text small>
                                    { meta.divisionName }
                                </Text>
                        }
                    </div>
                </Grid>

                <Grid item xs={6} md={4} lg={3} style={{ padding: 18 }}>
                    <div style={ styles.block }>
                        <Text bold small>
                            { i18n.t(tokens.titles.class) }
                        </Text>

                        {
                            classData[meta.classID].description ?
                                <Tooltip
                                    tip={ classData[meta.classID].description }
                                    placement="top-start"
                                >
                                    <Text small>
                                        { meta.className }
                                    </Text>
                                </Tooltip>
                                :
                                <Text small>
                                    { meta.className }
                                </Text>
                        }
                    </div>
                </Grid>

                <Grid item xs={6} md={4} lg={3} style={{ padding: 18 }}>
                    <div style={ styles.block }>
                        <Text bold small>
                            { i18n.t(tokens.labels.coverageType) }
                        </Text>

                        <Text small>
                            { localizationMaps.fromEnglish("coverageTypes", meta.coverageType) }
                        </Text>
                    </div>
                </Grid>

                <Grid item xs={6} md={4} lg={3} style={{ padding: 18 }}>
                    <div style={ styles.block }>
                        <Text bold small>
                            { i18n.t(tokens.labels.birthdate) }
                        </Text>

                        <Text small>
                            { dates.getStartOfDay(meta.birthdate, 'll', i18n.locale) }
                        </Text>
                    </div>
                </Grid>

                <Grid item xs={6} md={4} lg={3} style={{ padding: 18 }}>
                    <div style={ styles.block }>
                        <Text bold small>
                            { i18n.t(tokens.labels.maritalStatus) }
                        </Text>

                        <Text small>
                            { localizationMaps.fromEnglish("maritalStatuses", meta.maritlStat) || 'N/A' }
                        </Text>
                    </div>
                </Grid>

                <Grid item xs={6} md={4} lg={3} style={{ padding: 18 }}>
                    <div style={ styles.block }>
                        <Text bold small>
                            { i18n.t(tokens.labels.gender) }
                        </Text>

                        <Text small>
                            { localizationMaps.fromEnglish("genders", meta.gender) }
                        </Text>
                    </div>
                </Grid>

                <Grid item xs={6} md={4} lg={3} style={{ padding: 18 }}>
                    <div style={ styles.block }>
                        <Text bold small>
                            { i18n.t(tokens.labels.phoneNumber) }
                        </Text>

                        <Text small>
                            { meta.phone ? _parsePhoneNumber(meta.phone) : 'N/A' }
                        </Text>
                    </div>
                </Grid>

                <Grid item xs={6} md={4} lg={3} style={{ padding: 18 }}>
                    <div style={ styles.block }>
                        <Text bold small>
                            { i18n.t(tokens.labels.streetAddress) }
                        </Text>

                        <Text small customStyle={{ wordBreak: 'break-all' }}>
                            { meta.mailingAddress ? ` ${ address.suite } ${ address.firstLine } ` : 'N/A' }
                        </Text>
                    </div>
                </Grid>

                <Grid item xs={6} md={4} lg={3} style={{ padding: 18 }}>
                    <div style={ styles.block }>
                        <Text bold small>
                            { i18n.t(tokens.labels.account.cityTown) }
                        </Text>

                        <Text small>
                            { address.city || 'N/A' }
                        </Text>
                    </div>
                </Grid>

                <Grid item xs={6} md={4} lg={3} style={{ padding: 18 }}>
                    <div style={ styles.block }>
                        <Text bold small>
                            { i18n.t(tokens.labels.province) }
                        </Text>

                        <Text small>
                            { localizationMaps.fromEnglish("provinces", meta.province) }
                        </Text>
                    </div>
                </Grid>

                <Grid item xs={6} md={4} lg={3} style={{ padding: 18 }}>
                    <div style={ styles.block }>
                        <Text bold small>
                            Postal Code
                        </Text>

                        <Text small>
                            { address.postalCode || 'N/A' }
                        </Text>
                    </div>
                </Grid>

                <Grid item xs={6} md={4} lg={3} style={{ padding: 18 }}>
                    <div style={ styles.block }>
                        <Text bold small>
                            { i18n.t(tokens.labels.employees.earningsType) }
                        </Text>

                        <Text small>
                            { localizationMaps.fromEnglish("earningsTypes", meta.earningsType) }
                        </Text>
                    </div>
                </Grid>

                {
                    meta.earningsType === 'Salary' ?
                        <Grid item xs={6} md={4} lg={3} style={{ padding: 18 }}>
                            <div style={ styles.block }>
                                <Text bold small>
                                    { i18n.t(tokens.labels.employees.salary) }
                                </Text>

                                <Text small>
                                    ${ _numberWithCommas(meta.salary) }
                                </Text>
                            </div>
                        </Grid>
                        :
                        <Grid item xs={6} md={4} lg={3} style={{ padding: 18 }}>
                            <div style={ styles.block }>
                                <Text bold small>
                                    { i18n.t(tokens.labels.employees.hourlyWage) }
                                </Text>

                                <Text small>
                                    ${ _numberWithCommas(meta.hourlyWage) }
                                </Text>
                            </div>
                        </Grid>
                }

                <Grid item xs={6} md={4} lg={3} style={{ padding: 18 }}>
                    <div style={ styles.block }>
                        <Text bold small>
                            { i18n.t(tokens.labels.employees.hoursPerWeek) }
                        </Text>

                        <Text small>
                            { meta.hoursPerWeek || 0 }
                        </Text>
                    </div>
                </Grid>

                {
                    meta.payrollID ?
                        <Grid item xs={3} md={4} lg={3} style={{ padding: 18 }}>
                            <div style={ styles.block }>
                                <Text bold small>
                                    { i18n.t(tokens.labels.employees.payrollID) }
                                </Text>

                                <Text small>
                                    { meta.payrollID }
                                </Text>
                            </div>
                        </Grid>
                        :
                        null
                }

                {
                    <Grid item xs={3} md={4} lg={3} style={{ padding: 18 }}>
                        <div style={ styles.block }>
                            <Text bold small>
                                { i18n.t(tokens.labels.signup.language) }
                            </Text>
                            <Text small>
                                { meta.language === "fr" ? localizationMaps.fromEnglish("languages", "French") : localizationMaps.fromEnglish("languages", "English") }
                            </Text>
                        </div>
                    </Grid>
                }

                <Grid item xs={6} md={4} lg={3} style={{ padding: 18 }}>
                    <div style={ styles.block }>
                        <Text bold small>
                            { i18n.t(tokens.labels.employees.occupation) }
                        </Text>

                        <Text small>
                            { meta.title }
                        </Text>
                    </div>
                </Grid>

                <Grid item xs={12} md={6} lg={3} style={{ padding: 18 }}>
                    <div style={ styles.block }>
                        <Text bold small>
                            { i18n.t(tokens.labels.email) }
                        </Text>

                        <Text small customStyle={{ wordBreak: 'break-all' }}>
                            { meta.email }
                        </Text>
                    </div>
                </Grid>

                {
                    meta.paymentType !== null ? 
                        <Grid item xs={12} md={6} lg={3} style={{ padding: 18 }}>
                            <div style={ styles.block }>
                                <Text bold small>
                                    { i18n.t(tokens.labels.employees.memberPaymentType) }
                                </Text>
                                
                                <Text small>
                                    { meta.paymentType === "CC" ? i18n.t(tokens.labels.banking.creditCard) : i18n.t(tokens.labels.employees.eft) }
                                </Text>
                            </div>
                        </Grid>
                        :
                        null 
                }
                
            </Grid>

            {
                meta.enrollmentDate &&
                    <Grid
                        container
                        direction="row"
                        alignItems="center"
                        spacing={2}
                        sx={ styles.row }
                        wrap="wrap"
                    >
                        <Grid item xs={12} style={{ padding: 18 }}>
                            <div style={ styles.block }>
                                <Text veryBold medium>
                                    { i18n.t(tokens.labels.employees.coverageEffectiveDates) }:
                                </Text>
                            </div>
                        </Grid>

                        <Grid item xs={3} md={4} lg={3} style={{ padding: 18 }}>
                            <div style={ styles.block }>
                                <Text bold small>
                                    { i18n.t(tokens.titles.criticalIllness) }
                                </Text>

                                <Text small>
                                    { coverage.criticalIllness.benefitActive === 1 ? dates.getStartOfDay(coverage.criticalIllness.eligibilityPeriodEnds, 'll', i18n.locale) : i18n.t(tokens.titles.noCoverage) }
                                </Text>
                            </div>
                        </Grid>

                        <Grid item xs={3} md={4} lg={3} style={{ padding: 18 }}>
                            <div style={ styles.block }>
                                <Text bold small>
                                    { i18n.t(tokens.titles.accidentalDeathAndDismemberment) }
                                </Text>

                                <Text small>
                                    { coverage.deathDismemberment.benefitActive === 1 ? dates.getStartOfDay(coverage.deathDismemberment.eligibilityPeriodEnds, 'll', i18n.locale) : i18n.t(tokens.titles.noCoverage) }
                                </Text>
                            </div>
                        </Grid>

                        <Grid item xs={3} md={4} lg={3} style={{ padding: 18 }}>
                            <div style={ styles.block }>
                                <Text bold small>
                                    { i18n.t(tokens.titles.dental) }
                                </Text>

                                {
                                    hasWaived.includes('Dental') && meta.waiveFor === 'Self-Dependents' ?
                                        <Text small>
                                            { coverage.dental.benefitActive === 1 ? i18n.t(tokens.labels.employees.waived) : i18n.t(tokens.titles.noCoverage) }
                                        </Text>
                                        :
                                        <Text small>
                                            { coverage.dental.benefitActive === 1 ? dates.getStartOfDay(coverage.dental.eligibilityPeriodEnds, 'll', i18n.locale) : i18n.t(tokens.titles.noCoverage) }
                                        </Text>
                                }
                            </div>
                        </Grid>

                        <Grid item xs={3} md={4} lg={3} style={{ padding: 18 }}>
                            <div style={ styles.block }>
                                <Text bold small>
                                    { i18n.t(tokens.titles.dependentLife) }
                                </Text>

                                <Text small>
                                    { coverage.dependentLife.benefitActive === 1 ? dates.getStartOfDay(coverage.dependentLife.eligibilityPeriodEnds, 'll', i18n.locale) : i18n.t(tokens.titles.noCoverage) }
                                </Text>
                            </div>
                        </Grid>

                        <Grid item xs={3} md={4} lg={3} style={{ padding: 18 }}>
                            <div style={ styles.block }>
                                <Text bold small>
                                    { i18n.t(tokens.titles.drugs) }
                                </Text>

                                {
                                    hasWaived.includes('Health') && meta.waiveFor === 'Self-Dependents' ?
                                        <Text small>
                                            { coverage.drugs.benefitActive === 1 ? i18n.t(tokens.labels.employees.waived) : i18n.t(tokens.titles.noCoverage) }
                                        </Text>
                                        :
                                        <Text small>
                                            { coverage.drugs.benefitActive === 1 ? dates.getStartOfDay(coverage.drugs.eligibilityPeriodEnds, 'll', i18n.locale) : i18n.t(tokens.titles.noCoverage) }
                                        </Text>
                                }
                            </div>
                        </Grid>

                        <Grid item xs={3} md={4} lg={3} style={{ padding: 18 }}>
                            <div style={ styles.block }>
                                <Text bold small>
                                    { i18n.t(tokens.titles.employeeAssistance) }
                                </Text>

                                <Text small>
                                    { coverage.efap.benefitActive === 1 ? dates.getStartOfDay(coverage.efap.eligibilityPeriodEnds, 'll', i18n.locale) : i18n.t(tokens.titles.noCoverage) }
                                </Text>
                            </div>
                        </Grid>

                        <Grid item xs={3} md={4} lg={3} style={{ padding: 18 }}>
                            <div style={ styles.block }>
                                <div style={ styles.carrierBlock }>
                                    <Text bold small>
                                        { i18n.t(tokens.titles.virtualHealthCare) }
                                    </Text>

                                    {
                                        coverage.virtualHealthcare.carrier && coverage.virtualHealthcare.benefitActive === 1 &&
                                            <Text tiny light margin="0 0 0 5px">
                                                { coverage.virtualHealthcare.carrier }
                                            </Text>
                                    }
                                </div>

                                <Text small>
                                    { coverage.virtualHealthcare.benefitActive === 1 ? dates.getStartOfDay(coverage.virtualHealthcare.eligibilityPeriodEnds, 'll', i18n.locale) : i18n.t(tokens.titles.noCoverage) }
                                </Text>
                            </div>
                        </Grid>

                        <Grid item xs={3} md={4} lg={3} style={{ padding: 18 }}>
                            <div style={ styles.block }>
                                <div style={ styles.carrierBlock }>
                                    <Text bold small>
                                        { i18n.t(tokens.titles.financialAwareness) }
                                    </Text>

                                    {
                                        coverage.financialAwareness.carrier && coverage.financialAwareness.benefitActive === 1 &&
                                            <Text tiny light margin="0 0 0 5px">
                                                { coverage.financialAwareness.carrier }
                                            </Text>
                                    }
                                </div>

                                <Text small>
                                    { coverage.financialAwareness.benefitActive === 1 ? dates.getStartOfDay(coverage.financialAwareness.eligibilityPeriodEnds, 'll', i18n.locale) : i18n.t(tokens.titles.noCoverage) }
                                </Text>
                            </div>
                        </Grid>

                        <Grid item xs={3} md={4} lg={3} style={{ padding: 18 }}>
                            <div style={ styles.block }>
                                <Text bold small>
                                    { i18n.t(tokens.titles.lifeInsurance) }
                                </Text>

                                <Text small>
                                    { coverage.lifeInsurance.benefitActive === 1 ? dates.getStartOfDay(coverage.lifeInsurance.eligibilityPeriodEnds, 'll', i18n.locale) : i18n.t(tokens.titles.noCoverage) }
                                </Text>
                            </div>
                        </Grid>

                        <Grid item xs={3} md={4} lg={3} style={{ padding: 18 }}>
                            <div style={ styles.block }>
                                <Text bold small>
                                    { i18n.t(tokens.titles.longTermDisability) }
                                </Text>

                                <Text small>
                                    { coverage.ltd.benefitActive === 1 ? dates.getStartOfDay(coverage.ltd.eligibilityPeriodEnds, 'll', i18n.locale) : i18n.t(tokens.titles.noCoverage) }
                                </Text>
                            </div>
                        </Grid>

                        <Grid item xs={3} md={4} lg={3} style={{ padding: 18 }}>
                            <div style={ styles.block }>
                                <Text bold small>
                                    { i18n.t(tokens.titles.shortTermDisability) }
                                </Text>

                                <Text small>
                                    { coverage.std.benefitActive === 1 ? dates.getStartOfDay(coverage.std.eligibilityPeriodEnds, 'll', i18n.locale) : i18n.t(tokens.titles.noCoverage) }
                                </Text>
                            </div>
                        </Grid>

                        <Grid item xs={3} md={4} lg={3} style={{ padding: 18 }}>
                            <div style={ styles.block }>
                                <Text bold small>
                                    { i18n.t(tokens.titles.majorMedical) }
                                </Text>

                                {
                                    hasWaived.includes('Health') && meta.waiveFor === 'Self-Dependents' ?
                                        <Text small>
                                            { coverage.majorMedical.benefitActive === 1 ? i18n.t(tokens.labels.employees.waived) : i18n.t(tokens.titles.noCoverage) }
                                        </Text>
                                        :
                                        <Text small>
                                            { coverage.majorMedical.benefitActive === 1 ? dates.getStartOfDay(coverage.majorMedical.eligibilityPeriodEnds, 'll', i18n.locale) : i18n.t(tokens.titles.noCoverage) }
                                        </Text>
                                }
                            </div>
                        </Grid>

                        <Grid item xs={3} md={4} lg={3} style={{ padding: 18 }}>
                            <div style={ styles.block }>
                                <Text bold small>
                                    { i18n.t(tokens.titles.outOfCountry) }
                                </Text>

                                {
                                    hasWaived.includes('Health') && meta.waiveFor === 'Self-Dependents' ?
                                        <Text small>
                                            { coverage.outOfCountry.benefitActive === 1 ? i18n.t(tokens.labels.employees.waived) : i18n.t(tokens.titles.noCoverage) }
                                        </Text>
                                        :
                                        <Text small>
                                            { coverage.outOfCountry.benefitActive === 1 ? dates.getStartOfDay(coverage.outOfCountry.eligibilityPeriodEnds, 'll', i18n.locale) : i18n.t(tokens.titles.noCoverage) }
                                        </Text>
                                }
                            </div>
                        </Grid>

                        <Grid item xs={3} md={4} lg={3} style={{ padding: 18 }}>
                            <div style={ styles.block }>
                                <Text bold small>
                                    { i18n.t(tokens.titles.paramedicals) }
                                </Text>

                                {
                                    hasWaived.includes('Health') && meta.waiveFor === 'Self-Dependents' ?
                                        <Text small>
                                            { coverage.paramedicals.benefitActive === 1 ? i18n.t(tokens.labels.employees.waived) : i18n.t(tokens.titles.noCoverage) }
                                        </Text>
                                        :
                                        <Text small>
                                            { coverage.paramedicals.benefitActive === 1 ? dates.getStartOfDay(coverage.paramedicals.eligibilityPeriodEnds, 'll', i18n.locale) : i18n.t(tokens.titles.noCoverage) }
                                        </Text>
                                }
                            </div>
                        </Grid>

                        <Grid item xs={3} md={4} lg={3} style={{ padding: 18 }}>
                            <div style={ styles.block }>
                                <Text bold small>
                                    { i18n.t(tokens.titles.spendingAccounts) }
                                </Text>

                                <Text small>
                                    { coverage.spendingAccounts.meta?.benefitActive === 1 ? dates.getStartOfDay(coverage.spendingAccounts.meta.eligibilityPeriodEnds, 'll', i18n.locale) : i18n.t(tokens.titles.noCoverage) }
                                </Text>
                            </div>
                        </Grid>

                        <Grid item xs={3} md={4} lg={3} style={{ padding: 18 }}>
                            <div style={ styles.block }>
                                <Text bold small>
                                    { i18n.t(tokens.titles.vision) }
                                </Text>

                                {
                                    hasWaived.includes('Health') && meta.waiveFor === 'Self-Dependents' ?
                                        <Text small>
                                            { coverage.vision.benefitActive === 1 ? i18n.t(tokens.labels.employees.waived) : i18n.t(tokens.titles.noCoverage) }
                                        </Text>
                                        :
                                        <Text small>
                                            { coverage.vision.benefitActive === 1 ? dates.getStartOfDay(coverage.vision.eligibilityPeriodEnds, 'll', i18n.locale) : i18n.t(tokens.titles.noCoverage) }
                                        </Text>
                                }
                            </div>
                        </Grid>

                        <Grid item xs={3} md={4} lg={3} style={{ padding: 18 }}>
                            <div style={ styles.block }>
                                <Text bold small>
                                    { i18n.t(tokens.titles.waivedCoverage) }
                                </Text>

                                {
                                    hasWaived ?
                                        <Text small>
                                            { hasWaived === 'Health-Dental' ? i18n.t(tokens.titles.healthAndDental) : hasWaived }
                                        </Text>
                                        :
                                        <Text small>
                                            { i18n.t(tokens.buttons.no) }
                                        </Text>
                                }
                            </div>
                        </Grid>
                        <Grid item xs={12} style={{ paddingRight: 18, paddingLeft: 18, paddingTop: 18 }}>
                            <div style={ styles.block }>
                                <Text veryBold medium>
                                    Coverage Information:
                                </Text>
                            </div>
                        </Grid>
                        { meta.eligibilityOverrideDate ?
                            <Grid item xs={12}>
                                <div style={ styles.effectiveDateDisplay }>
                                    <InfoIcon aria-label="info" style={{ fontSize: 20, color: theme.palette.primary.main, marginRight: 10 }} />
                                    <Text AvMedium customStyle={{ marginRight: 6.5 }}>
                                        { i18n.t(tokens.labels.employees.coverageInformation) }
                                    </Text>
                                    <Text bold AvMedium>{ dates.getStartOfDay(meta.eligibilityOverrideDate, "ll", i18n.locale) }</Text>
                                </div>
                            </Grid>
                            :
                            null
                        }
                        {
                            Boolean(hasWaived) &&
                                <Grid item xs={3} md={4} lg={3} style={{ padding: 18 }}>
                                    <div style={ styles.block }>
                                        <Text bold small>
                                            { i18n.t(tokens.titles.waivedCoverageFor) }
                                        </Text>

                                        {
                                            meta.waiveFor === 'Self-Dependents' ?
                                                <Text small>
                                                    { i18n.t(tokens.labels.employees.memberAndDependents) }
                                                </Text>
                                                :
                                                <Text small>
                                                    { i18n.t(tokens.labels.employees.dependentsOnly) }
                                                </Text>
                                        }
                                    </div>
                                </Grid>
                        }
                    </Grid>
            }
        </div>
    );
};

export default (PersonalInfoBlock);